import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { useLocation } from "wouter";
import * as customerApi from "@/lib/customer-api";
import { getCustomerIdFromToken } from "@/lib/customer-order-queries";
import {
  queryClient,
  setAuthFailureHandler,
} from "@/lib/queryClient";
import { z } from "zod";

const receivedSessionSchema = z.object({
  token: z.string().trim().min(1),
  refreshToken: z.string().trim().min(1),
  customer: z.object({
    id: z.string().min(1),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    isVerified: z.boolean(),
    privacyAccepted: z.boolean().optional(),
    marketingConsent: z.boolean().optional(),
  }).passthrough(),
});

export interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  fiscalCode?: string;
  vatNumber?: string;
  companyName?: string;
  isVerified: boolean;
  privacyAccepted?: boolean;
  marketingConsent?: boolean;
}

interface CustomerAuthContextType {
  customer: Customer | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  activateSession: (session: unknown) => Promise<void>;
  logout: () => void;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    privacyAccepted: boolean;
    marketingConsent?: boolean;
  }) => Promise<{ message: string }>;
  refreshToken: () => Promise<void>;
  updateCustomer: (data: Partial<Customer>) => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | null>(null);

function isCustomerPrivateQuery(queryKey: readonly unknown[]): boolean {
  const root = queryKey[0];
  return typeof root === "string" && (
    root.startsWith("/api/cart") ||
    root.startsWith("/api/orders") ||
    root.startsWith("/api/customer") ||
    root.startsWith("/api/auth/customer")
  );
}

function customerQueryFilters() {
  return {
    predicate: (query: { queryKey: readonly unknown[] }) =>
      isCustomerPrivateQuery(query.queryKey),
  };
}

async function clearCustomerPrivateQueries(): Promise<void> {
  const filters = customerQueryFilters();
  await queryClient.cancelQueries(filters);
  if (typeof queryClient.removeQueries === "function") {
    queryClient.removeQueries(filters);
  } else {
    queryClient.clear();
  }
}

function resetCustomerPrivateQueries(): void {
  const filters = customerQueryFilters();
  void queryClient.cancelQueries(filters);
  if (typeof queryClient.removeQueries === "function") {
    queryClient.removeQueries(filters);
  } else {
    queryClient.clear();
  }
}

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error("useCustomerAuth must be used within a CustomerAuthProvider");
  }
  return context;
};

interface CustomerAuthProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider = ({ children }: CustomerAuthProviderProps) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();
  const sessionRevision = useRef(0);

  useEffect(() => {
    const revision = sessionRevision.current;
    const storedToken = localStorage.getItem("customer_token");
    const storedRefreshToken = localStorage.getItem("customer_refresh_token");
    
    if (storedToken) {
      setToken(storedToken);
      customerApi.setCustomerAuthToken(storedToken);
      
      customerApi.getCurrentCustomer()
        .then(customerData => {
          if (revision === sessionRevision.current) {
            setCustomer(customerData);
          }
        })
        .catch(async () => {
          if (revision !== sessionRevision.current) return;
          if (storedRefreshToken) {
            try {
              await refreshTokenHandler(storedRefreshToken, revision);
            } catch {
              if (revision === sessionRevision.current) logout();
            }
          } else {
            logout();
          }
        })
        .finally(() => {
          if (revision === sessionRevision.current) setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const refreshTokenHandler = async (refreshTokenValue: string, revision = sessionRevision.current) => {
    const response = await customerApi.refreshToken(refreshTokenValue);
    if (revision !== sessionRevision.current) return;
    localStorage.setItem("customer_token", response.token);
    localStorage.setItem("customer_refresh_token", response.refreshToken);
    setToken(response.token);
    customerApi.setCustomerAuthToken(response.token);
    
    const customerData = await customerApi.getCurrentCustomer();
    if (revision === sessionRevision.current) {
      setCustomer(customerData);
    }
  };

  // Il completamento registrazione restituisce già una sessione, non credenziali.
  const activateSession = async (input: unknown) => {
    const parsed = receivedSessionSchema.safeParse(input);
    if (!parsed.success) {
      throw new Error("Accesso automatico non disponibile. Prova ad accedere con la password appena scelta.");
    }
    const revision = ++sessionRevision.current;
    await clearCustomerPrivateQueries();
    if (revision !== sessionRevision.current) {
      throw new Error("Sessione cambiata. Effettua nuovamente l'accesso.");
    }
    try {
      localStorage.setItem("customer_token", parsed.data.token);
      localStorage.setItem("customer_refresh_token", parsed.data.refreshToken);
    } catch {
      logout();
      throw new Error("Impossibile salvare la sessione. Abilita l'archiviazione del browser e accedi con la password scelta.");
    }
    customerApi.setCustomerAuthToken(parsed.data.token);
    setToken(parsed.data.token);
    setCustomer(parsed.data.customer as Customer);
    setIsLoading(false);
  };

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    const revision = ++sessionRevision.current;
    await clearCustomerPrivateQueries();
    const response = await customerApi.login(email, password, rememberMe);
    if (revision !== sessionRevision.current) {
      throw new Error("Sessione cambiata. Effettua nuovamente l'accesso.");
    }
    
    localStorage.setItem("customer_token", response.token);
    localStorage.setItem("customer_refresh_token", response.refreshToken);
    
    setToken(response.token);
    setCustomer(response.customer);
    customerApi.setCustomerAuthToken(response.token);
  };

  const register = async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    privacyAccepted: boolean;
    marketingConsent?: boolean;
  }) => {
    return customerApi.register(data);
  };

  const logout = () => {
    sessionRevision.current++;
    resetCustomerPrivateQueries();
    try {
      localStorage.removeItem("customer_token");
      localStorage.removeItem("customer_refresh_token");
    } catch {
      // L'archiviazione può essere disabilitata: revocare comunque lo stato in memoria.
    } finally {
      setCustomer(null);
      setToken(null);
      setIsLoading(false);
      customerApi.clearCustomerAuthToken();
    }
  };

  const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem("customer_refresh_token");
    if (!storedRefreshToken) {
      throw new Error("No refresh token available");
    }
    
    await refreshTokenHandler(storedRefreshToken);
  };

  const updateCustomer = async (data: Partial<Customer>) => {
    await customerApi.updateProfile(data);
    if (customer) {
      setCustomer({ ...customer, ...data });
    }
  };

  // Logout "soft" quando il transport unificato (queryClient) non riesce a
  // rinnovare il token cliente: pulisce lo stato; le route protette
  // reindirizzano da sole quando `customer` diventa null.
  // La stessa recinzione vale per un cambio account in un'altra scheda:
  // l'evento storage non deve cancellare le nuove credenziali, ma deve
  // rimuovere subito la cache e nascondere l'identità precedente.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== "customer_token" && event.key !== "customer_refresh_token") return;
      if (event.key === "customer_refresh_token") return;

      const nextToken = localStorage.getItem("customer_token");
      const nextCustomerId = getCustomerIdFromToken(nextToken);
      if (nextCustomerId && nextCustomerId === customer?.id) {
        setToken(nextToken);
        customerApi.setCustomerAuthToken(nextToken);
        return;
      }

      const revision = ++sessionRevision.current;
      resetCustomerPrivateQueries();
      setCustomer(null);
      setToken(nextToken);
      setIsLoading(!!nextToken && !!nextCustomerId);
      customerApi.setCustomerAuthToken(nextToken);

      if (!nextToken || !nextCustomerId) {
        setIsLoading(false);
        return;
      }

      customerApi.getCurrentCustomer()
        .then(customerData => {
          if (revision === sessionRevision.current && customerData.id === nextCustomerId) {
            setCustomer(customerData);
          }
        })
        .catch(() => {
          if (revision === sessionRevision.current) setCustomer(null);
        })
        .finally(() => {
          if (revision === sessionRevision.current) setIsLoading(false);
        });
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [customer?.id]);

  useEffect(() => {
    setAuthFailureHandler("customer", () => logout());
    return () => setAuthFailureHandler("customer", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CustomerAuthContext.Provider value={{
      customer,
      token,
      isLoading,
      isAuthenticated: !!customer,
      login,
      activateSession,
      logout,
      register,
      refreshToken,
      updateCustomer,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

interface CustomerProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export const CustomerProtectedRoute = ({ children, redirectTo = "/accedi" }: CustomerProtectedRouteProps) => {
  const { customer, isLoading } = useCustomerAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !customer) {
      setLocation(redirectTo);
    }
  }, [customer, isLoading, redirectTo, setLocation]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0f1419]">
        <div className="text-[#9c9e9f]">Caricamento...</div>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  return <>{children}</>;
};
